import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Card,
  Overline,
  SectionHeader,
  Tag,
} from "@/components/cathedral/Primitives";
import LensDriftChart from "@/components/cathedral/LensDriftChart";
import {
  CHAMBER_OF_ECHOES_URL,
  RING_OF_12_COUNCIL_URL,
  RING_OF_12_COUNCIL_ALT,
  RING_OF_12_SYNTH_URL,
  RING_OF_12_SYNTH_ALT,
} from "@/lib/personaFaces";

export default function Deliberations() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.deliberations({ limit: 50 }).then((r) => {
      setItems(r.deliberations || []);
      if (r.deliberations?.length) setActive(r.deliberations[0]);
    });
  }, []);

  // Architect Asset Mandate: official Chamber of Echoes background.
  const COUNCIL_BG = CHAMBER_OF_ECHOES_URL;

  return (
    <section
      data-testid="deliberations-page"
      className="relative px-8 lg:px-12 py-12 max-w-screen-2xl"
      style={{ minHeight: "calc(100vh - 120px)" }}
    >
      {/* ── Council Room atmospheric background ─────────────────── */}
      <div
        aria-hidden
        data-testid="council-room-bg"
        className="pointer-events-none absolute inset-0 -z-10 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(2,3,12,0.70) 0%, rgba(2,3,12,0.92) 100%), url('${COUNCIL_BG}')`,
          backgroundSize: "cover",
          backgroundPosition: "center top",
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        }}
      />
      {/* Subtle vignette + neon edge to harmonize with cyber-gothic palette */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(167,86,255,0.18) 0%, rgba(0,0,0,0) 60%)," +
            " linear-gradient(180deg, rgba(0,240,255,0.06) 0%, rgba(255,43,214,0.04) 100%)",
        }}
      />

      <SectionHeader
        overline="DIR-072 · Ring of Perspectives"
        title="Ring Deliberations"
        description="Every significant task passes through the Ring of Six. Deep questions activate the Ring of Twelve. Urgent matters use the Fast Ring, with mandatory full review after."
      />

      {/* ── RING OF 12 · Council hero anchor (Architect Asset Mandate) ── */}
      <div
        data-testid="ring12-council-hero"
        className="relative my-10 rounded-2xl overflow-hidden cursor-pointer group"
        onClick={() => setActive(items[0] || null)}
        style={{
          aspectRatio: "21 / 9",
          border:    "1px solid rgba(255,189,0,0.40)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.55), 0 0 24px rgba(167,86,255,0.20)",
        }}
      >
        <img
          src={RING_OF_12_COUNCIL_URL}
          alt={RING_OF_12_COUNCIL_ALT}
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(2,3,12,0.20) 0%, rgba(2,3,12,0.45) 55%, rgba(2,3,12,0.92) 100%)",
          }}
        />
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.32em] mb-2" style={{ color: "#ffb200", textShadow: "0 0 10px rgba(255,189,0,0.6)" }}>
            ◆ RING OF 12 · COUNCIL HALL · GOVERNANCE LAYER
          </div>
          <h2
            className="font-display font-light leading-tight"
            style={{
              fontSize:   "clamp(28px, 4.2vw, 48px)",
              color:      "#fff7e0",
              textShadow: "0 2px 16px rgba(0,0,0,0.75)",
            }}
          >
            The twelve minds convene.
          </h2>
          <p className="mt-3 max-w-2xl font-mono text-[12px] leading-relaxed" style={{ color: "rgba(255,247,224,0.78)" }}>
            Every deep deliberation passes through this council. Click any entry below to see who deliberated, what they saw, and where they disagreed.
          </p>
        </div>
      </div>

      <div className="mb-10">
        <LensDriftChart />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
        <Card className="p-0 lg:col-span-5">
          <header className="px-6 py-4 border-b border-[var(--chrome)]">
            <Overline>Recent Deliberations · {items.length}</Overline>
          </header>
          <ul className="divide-y divide-slate-200 max-h-[75vh] overflow-y-auto">
            {items.map((d) => (
              <li
                key={d.id}
                onClick={() => setActive(d)}
                data-testid={`delib-row-${d.id}`}
                className={`px-6 py-5 cursor-pointer hover:bg-[rgba(0,240,255,0.06)] ${
                  active?.id === d.id ? "bg-[rgba(0,240,255,0.06)] border-l-2 border-[var(--cyan)]" : "border-l-2 border-transparent"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Tag tone="fact">{(d.ring_depth || "—").toUpperCase()}</Tag>
                  <Tag>{d.status || "—"}</Tag>
                  <span className="font-mono text-[10px] text-[var(--text-3)]">{d.node_id}</span>
                </div>
                <div className="text-sm text-[var(--text-2)] truncate">{d.synthesis || "—"}</div>
                <div className="font-mono text-[10px] text-[var(--text-3)] mt-1">
                  CONFIDENCE · {d.confidence != null ? `${Math.round(d.confidence * 100)}%` : "—"} · {new Date(d.timestamp).toLocaleString()}
                </div>
              </li>
            ))}
            {items.length === 0 && (
              <li className="p-6 font-mono text-xs text-[var(--text-3)]">No deliberations yet.</li>
            )}
          </ul>
        </Card>

        <div className="lg:col-span-7">
          {active ? (
            <Card className="relative p-10 overflow-hidden">
              {/* ── RING OF 12 · Synth-in-Mind backdrop (cognition layer) ── */}
              <div
                aria-hidden="true"
                data-testid="ring12-synth-bg"
                className="absolute inset-0 pointer-events-none -z-10"
                style={{
                  backgroundImage:
                    `linear-gradient(180deg, rgba(2,3,12,0.78) 0%, rgba(2,3,12,0.92) 100%), url('${RING_OF_12_SYNTH_URL}')`,
                  backgroundSize:     "cover",
                  backgroundPosition: "center",
                  backgroundRepeat:   "no-repeat",
                  opacity: 0.85,
                }}
                title={RING_OF_12_SYNTH_ALT}
              />
              <Overline>Synthesis</Overline>
              <p className="font-serif text-3xl font-light tracking-tight mt-3 text-[var(--text)] leading-tight whitespace-pre-wrap">
                {active.synthesis || "—"}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <Tag tone="fact">{(active.ring_depth || "—").toUpperCase()} RING</Tag>
                <Tag>{active.codex_impact || "—"} IMPACT</Tag>
                <Tag>{active.nexus_alignment || "—"}</Tag>
              </div>

              <div className="mt-10">
                <Overline>Lenses Engaged</Overline>
                <ul className="mt-4 space-y-3">
                  {(active.perspectives || []).map((p, i) => (
                    <li key={`${p.lens || "lens"}-${i}`} className="p-4 border border-[var(--chrome)]">
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--text-3)]">
                        Lens {i + 1}
                      </div>
                      <div className="font-serif text-xl font-medium text-[var(--text)] mt-1">{p.lens}</div>
                      <div className="text-sm text-[var(--text-3)] mt-1">{p.question}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </Card>
          ) : (
            <Card className="p-12">
              <p className="font-serif text-3xl font-light text-[var(--text-3)]">
                Select a deliberation.
              </p>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}
